import "@/App.css";
import StudioPage from "@/pages/StudioPage";
import ThemedToaster from "@/components/ThemedToaster";
import { useLayoutEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Shell from "@/components/Shell";
import ErrorBoundary from "@/components/ErrorBoundary";
import LoadError from "@/components/LoadError";
import { resetRouteRequests } from "@/lib/api";
import Dashboard from "@/pages/Dashboard";
import PatientDetail from "@/pages/PatientDetail";
import NewAssessment from "@/pages/NewAssessment";
import AssessmentWorkspace from "@/pages/AssessmentWorkspace";
import CalendarPage from "@/pages/CalendarPage";
import RemindersPage from "@/pages/RemindersPage";
import PortalPage from "@/pages/PortalPage";
import SignPublicPage from "@/pages/SignPublicPage";
import PhotoGuidePage from "@/pages/PhotoGuidePage";
import ComparePage from "@/pages/ComparePage";
import ReportsPage from "@/pages/ReportsPage";
import IntakePage from "@/pages/IntakePage";
import LibraryPage from "@/pages/LibraryPage";
import PatientsPage from "@/pages/PatientsPage";
import LoginPage from "@/pages/LoginPage";
import AuthCallbackPage from "@/pages/AuthCallbackPage";
import SettingsPage from "@/pages/SettingsPage";
import { AuthProvider, useAuth } from "@/lib/auth";
import { AppSettingsProvider } from "@/lib/appSettings";

function WithShell({ children }) {
  return <Shell>{children}</Shell>;
}

function RequireAuth({ children }) {
  const { user, loading, authError, refresh } = useAuth();
  const { pathname, search } = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500" data-testid="auth-loading">
        <div className="font-display text-xl tracking-[0.22em]">MIO</div>
      </div>
    );
  }
  if (!user && authError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadError testId="auth-network-error" message="Não foi possível confirmar a sessão. Verifique a ligação e tente novamente." onRetry={refresh} />
      </div>
    );
  }
  if (!user) {
    try { sessionStorage.setItem("mio:returnTo", pathname + search); } catch { /* */ }
    return <Navigate to="/login" replace state={{ from: pathname }} />;
  }
  return children;
}

function AppRoutes() {
  const { pathname } = useLocation();
  const isPortal = pathname.startsWith("/portal/") || pathname.startsWith("/sign/") || pathname === "/guia-fotografico";
  const isAuth = pathname.startsWith("/login") || pathname.startsWith("/auth/callback");

  // Corre antes dos efeitos da nova página: cancela leituras pendentes da página anterior.
  useLayoutEffect(() => { resetRouteRequests(); }, [pathname]);

  if (isPortal) {
    return (
      <ErrorBoundary resetKey={pathname} homeHref={pathname} homeLabel="Recarregar">
        <Routes>
          <Route path="/portal/:token" element={<PortalPage />} />
          <Route path="/sign/:token" element={<SignPublicPage />} />
          <Route path="/guia-fotografico" element={<PhotoGuidePage />} />
        </Routes>
      </ErrorBoundary>
    );
  }
  if (isAuth) {
    return (
      <ErrorBoundary resetKey={pathname} homeHref="/login" homeLabel="Voltar ao login">
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
        </Routes>
      </ErrorBoundary>
    );
  }
  return (
    <RequireAuth>
      <WithShell>
        <ErrorBoundary resetKey={pathname}>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/dashboard" element={<Navigate to="/" replace />} />
          <Route path="/patients" element={<PatientsPage />} />
          <Route path="/patients/:id" element={<PatientDetail />} />
          <Route path="/patients/:id/intake" element={<IntakePage />} />
          <Route path="/patients/:id/assessments/new" element={<NewAssessment />} />
          <Route path="/patients/:id/compare" element={<ComparePage />} />
          <Route path="/assessments/:id" element={<AssessmentWorkspace />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/library" element={<LibraryPage />} />
          <Route path="/calendar" element={<CalendarPage />} />
          <Route path="/reminders" element={<RemindersPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/studio" element={<StudioPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </ErrorBoundary>
      </WithShell>
    </RequireAuth>
  );
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary homeHref="/" homeLabel="Recarregar">
      <BrowserRouter>
        <AuthProvider>
          <AppSettingsProvider>
            <AppRoutes />
            <ThemedToaster />
          </AppSettingsProvider>
        </AuthProvider>
      </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

export default App;
